import React, { useEffect, useMemo, useState } from 'react';
import {
	useNormalWorkplacesQuery,
	useWorkerInformationQuery,
} from '../../../../graphql/graphql';
import Select, { SelectValue } from '../../../../Components/Base/Select/Select';
import Loading from '../../../../Components/Loading';
import { useAppContext } from '../../../../Services/AppContext';
// @ts-ignore
import styles from './ActiveWorkplaceSelect.module.scss';
import NetteApiClient from '../../../../Services/NetteApiClient';
import { joinClasses } from '../../../../Utils/className.util';
import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../Base/Hooks/useApiClient';
import ConfirmationModal from '../../../Base/Components/Confirmation/ConfirmationModal';
import { Theme } from '../../../../Enums/Theme.enum';
import Button from '../../../../Components/Button/Button';
import useTranslation from '../../../../Hooks/useTranslation';
import { useDispatch } from 'react-redux';
import { setPatientDiagnosis } from '../../../../store/reducers/patientMetaSlice';
import { setUserAllowedWorkplaces } from '../../../../store/reducers/userSlice';
interface ActiveWorkplaceSelectItem extends SelectValue {
	rootName: string;
	address: string;
	isFavorite: boolean;
}

const ActiveWorkplaceSelect: React.FC = () => {
	const { t } = useTranslation();
	const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
	const [selectedWorkplaceId, setSelectedWorkplaceId] = useState<string | null>(
		null,
	);
	const { workplaceId, changeWorkPlace, userData, socket } = useAppContext();
	const normalWorkplaces = useNormalWorkplacesQuery({ take: 999 });
	const workerInformation = useWorkerInformationQuery({
		userId: userData.id,
	});
	const dispatch = useDispatch();

	const options: ActiveWorkplaceSelectItem[] = useMemo(() => {
		const items: ActiveWorkplaceSelectItem[] = [];
		const allowedWorkplaces: { id: string; name: string }[] = [];

		for (const workplace of normalWorkplaces.data?.workplaces?.data || []) {
			if (workplace) {
				items.push({
					value: workplace.id,
					label: workplace.doctorDisplayName || '',
					rootName: workplace?.rootWorkplace?.doctorDisplayName ?? '',
					address: workplace?.address?.fullAddress ?? '',
					isFavorite: !!workerInformation.data?.user?.meta?.find(
						item =>
							item?.key === 'favouriteWorkplaceId' &&
							item.value === workplace.id,
					),
				});
				allowedWorkplaces.push({
					id: workplace.id,
					name: workplace.doctorDisplayName || '',
				});
			}
		}

		dispatch(setUserAllowedWorkplaces(allowedWorkplaces));
		return items;
	}, [normalWorkplaces.data, workerInformation.data]);

	const handleChangeWorkplace = (val: SelectValue) => {
		if (changeWorkPlace) {
			try {
				//console.log('emit userChangeWorkplace', val.value);
				socket.emit('userChangeWorkplace', {
					newWorkplaceId: val.value,
					oldWorkplaceId: userData.workplaceId,
					userId: userData.id,
				});
			} catch (e) {}

			NetteApiClient.get(changeWorkPlace.replace('__ID__', val.value));
		}
	};

	const CustomOption = ({ innerProps, data, isDisabled, isSelected }: any) => {
		const { userData } = useAppContext();
		const { displayError } = useApiClient();
		const handleMarkAsFavourite = async (workplaceId: string) => {
			try {
				const result: any = await apiClient.post(`/user/${userData.id}/meta/`, {
					key: 'favouriteWorkplaceId',
					value: workplaceId,
				});
				setSelectedWorkplaceId(workplaceId);
				setIsConfirmationOpen(true);
				workerInformation.refetch();
			} catch (e: any) {
				displayError(e);
			}
		};

		const starIcon = useMemo(() => {
			if (data.isFavorite) {
				return '/dist/icons/star-solid-yellow.svg';
			}
			return '/dist/icons/star-outline-grey.svg';
		}, []);

		const handleSelect = () => {
			innerProps.onClick();
		};

		return !isDisabled ? (
			<div className={styles.option}>
				<div
					className={joinClasses([
						styles.item,
						isSelected ? styles.active : '',
					])}>
					<div className={styles.nameWrapper}>
						<div className={styles.name} onClick={handleSelect}>
							{data.label}
						</div>
						<div
							className={styles.star}
							onClick={() => handleMarkAsFavourite(data.value)}>
							<img src={starIcon} alt='star' />
						</div>
					</div>
					{data.address && (
						<div className={styles.address} onClick={handleSelect}>
							{data.address}
						</div>
					)}
					{data.rootName && (
						<div className={styles.rootName} onClick={handleSelect}>
							{data.rootName}
						</div>
					)}
				</div>
			</div>
		) : null;
	};

	const handleAfterConfirmationClose = () => {
		setIsConfirmationOpen(false);
		if (changeWorkPlace && selectedWorkplaceId) {
			NetteApiClient.get(
				changeWorkPlace.replace('__ID__', selectedWorkplaceId),
			);
		}
	};

	if (normalWorkplaces.isLoading || workerInformation.isLoading) {
		return <Loading />;
	}

	return (
		<>
			<div className='activeWorkplaceSelect'>
				<Select
					options={options}
					onChange={handleChangeWorkplace}
					value={options.find(item => item.value === workplaceId)}
					components={{
						Option: CustomOption,
					}}
				/>
			</div>
			<ConfirmationModal
				title='component.activeWorkplaceSelect.confirmation.title'
				text={t('component.activeWorkplaceSelect.confirmation.text') ?? ''}
				isOpen={isConfirmationOpen}
				onClose={handleAfterConfirmationClose}>
				<Button
					onClick={handleAfterConfirmationClose}
					title={'base.understand'}
					theme={Theme.GREEN}
				/>
			</ConfirmationModal>
		</>
	);
};

export default ActiveWorkplaceSelect;
